import { startOfYear, subWeeks } from 'date-fns';

/** Shared feed filters — Badges Feed chips + profile /feed time range. */

/**
 * Last Week / In <calendar year> / All (`null`).
 * Badges Feed always uses week|year; profile /feed defaults to All (`null`).
 */
export type FeedTimeRange = 'week' | 'year' | null;
export type FeedRarityFilter = 'rare' | 'legendary' | null;
/** Journeys / New Badges — one or none. */
export type FeedTopicFilter = 'journeys' | 'newBadges' | null;

export type FeedFilterState = {
  range: FeedTimeRange;
  /** Rare+ / Legendary+ — one or none. */
  rarity: FeedRarityFilter;
  /** Journeys / New Badges — one or none. */
  topic: FeedTopicFilter;
};

/** Badges Feed default — Last Week selected, no rarity/topic chips. */
export const DEFAULT_FEED_FILTER: FeedFilterState = {
  range: 'week',
  rarity: null,
  topic: null,
};

/** Profile /feed — All time, no chips. */
export const PROFILE_FEED_FILTER: FeedFilterState = {
  range: null,
  rarity: null,
  topic: null,
};

let currentFilter: FeedFilterState = { ...DEFAULT_FEED_FILTER };
const listeners = new Set<() => void>();

const sameFilter = (a: FeedFilterState, b: FeedFilterState): boolean =>
  a.range === b.range && a.rarity === b.rarity && a.topic === b.topic;

export const getFeedFilter = (): FeedFilterState => currentFilter;

export const setFeedFilter = (filter: FeedFilterState): void => {
  if (sameFilter(currentFilter, filter)) return;
  currentFilter = filter;
  listeners.forEach((listener) => listener());
};

export const patchFeedFilter = (patch: Partial<FeedFilterState>): void => {
  setFeedFilter({ ...currentFilter, ...patch });
};

export const resetFeedFilter = (): void => {
  setFeedFilter({ ...DEFAULT_FEED_FILTER });
};

export const subscribeFeedFilter = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** Inclusive lower bound: rolling 7 days, or local midnight Jan 1 of the current calendar year. */
export const feedCutoff = (range: FeedTimeRange, now: Date = new Date()): Date | null => {
  if (range == null) return null;
  if (range === 'week') return subWeeks(now, 1);
  return startOfYear(now);
};
